import { useEffect, useState } from "react";
import api from "../lib/api";
import { Download } from "lucide-react";

export default function KFS() {
  const [lang, setLang] = useState("en");
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/policies/kfs/content", { params: { language: lang } }).then((r) => setContent(r.data));
  }, [lang]);

  async function downloadPdf() {
    const { data } = await api.get(`/pdfs/kfs/${lang}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nyoksure_kfs_${lang}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-8 space-y-6" data-testid="kfs-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#c15337]">Disclosure</div>
          <h1 className="font-heading text-3xl font-bold mt-1 text-[#114232]">Key Facts Statement</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-[#114232]/15 rounded-full overflow-hidden text-sm bg-white">
            {[["en", "English"], ["sw", "Kiswahili"]].map(([code, label]) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                data-testid={`kfs-lang-${code}`}
                className={`px-4 py-1.5 transition-colors ${
                  lang === code ? "bg-[#114232] text-[#f4f2eb]" : "hover:bg-[#114232]/5 text-[#5c6360]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={downloadPdf}
            data-testid="kfs-download-pdf"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-[#114232]/15 rounded-md bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> PDF
          </button>
        </div>
      </header>

      {content && (
        <article className="bg-white border border-border rounded-md p-8 space-y-6 max-w-3xl">
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">
              Version {content.version}
            </div>
            <h2 className="font-heading text-2xl font-bold">{content.title}</h2>
          </div>

          <Section title={lang === "sw" ? "Faida Kuu za Bima" : "Main Cover Benefits"}
                   items={content.main_cover_benefits} />
          <Section title={lang === "sw" ? "Yasiyojumuishwa" : "Key Exclusions"}
                   items={content.key_exclusions} />
          <div className="space-y-2">
            <h3 className="font-heading text-base font-semibold">
              {lang === "sw" ? "Kipindi cha Kupoa" : "Cooling-off Period"}
            </h3>
            <p className="text-sm text-stone-700 leading-relaxed">{content.cooling_off_period}</p>
          </div>
          <div className="space-y-2">
            <h3 className="font-heading text-base font-semibold">
              {lang === "sw" ? "Vikomo vya Madai" : "Claims Limits"}
            </h3>
            <ul className="text-sm space-y-1">
              {Object.entries(content.claims_limits).map(([tier, lim]) => (
                <li key={tier} className="font-mono">
                  <span className="capitalize">{tier.replace(/_/g, " ")}</span>: {lim}
                </li>
              ))}
            </ul>
          </div>
          <Section title={lang === "sw" ? "Wajibu wa Udanganyifu" : "Fraud Obligations"}
                   items={content.fraud_obligations} />
        </article>
      )}
    </div>
  );
}

function Section({ title, items }) {
  return (
    <div className="space-y-2">
      <h3 className="font-heading text-base font-semibold">{title}</h3>
      <ul className="text-sm space-y-1.5 list-disc pl-5 text-stone-700">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}
